/**
 * Payload Encryption Utility
 *
 * Optional AES-256-GCM transport obfuscation for API payloads (Web Crypto).
 * The key comes from `VITE_PAYLOAD_ENCRYPTION_KEY` and is **embedded in the SPA
 * bundle** — it is not a secret from the client. This is **not** end-to-end or
 * at-rest encryption; CRM notes remain plaintext in D1.
 *
 * Purpose: make casual DevTools inspection slightly harder when enabled.
 *
 * @example
 * ```typescript
 * import { payloadEncryption } from '../utils/encryption.util';
 *
 * // Encrypt request body
 * const encrypted = await payloadEncryption.encrypt({ userId: 123 });
 *
 * // Decrypt response body
 * const decrypted = await payloadEncryption.decrypt(encryptedResponse);
 * ```
 */

import { env } from '../config/env';
import { logger } from './logger';

/**
 * Encrypted payload structure sent over the wire
 */
export interface EncryptedPayload {
  /** Base64-encoded encrypted data */
  data: string;
  /** Base64-encoded initialization vector */
  iv: string;
  /** Encryption version for future key rotation support */
  v: number;
}

/**
 * Internal type for decrypted payloads
 */
export type DecryptedPayload = Record<string, unknown>;

/**
 * Payload Encryption class using Web Crypto API
 */
class PayloadEncryption {
  private key: CryptoKey | null = null;
  private enabled: boolean;
  private keyPromise: Promise<CryptoKey> | null = null;
  private readonly ALGORITHM = 'AES-GCM';
  private readonly KEY_LENGTH = 256;
  private readonly IV_LENGTH = 12; // Recommended for GCM
  private readonly VERSION = 1;

  constructor() {
    this.enabled = env.enablePayloadEncryption;
  }

  /**
   * Check if payload encryption is enabled
   */
  public isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Initialize the encryption key from environment variable
   * Uses a singleton pattern to ensure key is only imported once
   */
  private async initializeKey(): Promise<CryptoKey> {
    if (this.key) {
      return this.key;
    }

    // Prevent multiple concurrent key imports
    if (this.keyPromise) {
      return this.keyPromise;
    }

    this.keyPromise = this.importKey();
    this.key = await this.keyPromise;
    this.keyPromise = null;

    return this.key;
  }

  /**
   * Import the encryption key from base64-encoded environment variable
   */
  private async importKey(): Promise<CryptoKey> {
    const keyBase64 = env.payloadEncryptionKey;

    if (!keyBase64) {
      throw new Error('Payload encryption key not configured');
    }

    try {
      // Decode base64 key to ArrayBuffer
      const keyBytes = Uint8Array.from(atob(keyBase64), (c) => c.charCodeAt(0));

      if (keyBytes.length !== 32) {
        throw new Error(`Invalid key length: expected 32 bytes, got ${keyBytes.length}`);
      }

      // Import as CryptoKey for AES-GCM
      const key = await crypto.subtle.importKey(
        'raw',
        keyBytes,
        { name: this.ALGORITHM, length: this.KEY_LENGTH },
        false, // Not extractable
        ['encrypt', 'decrypt']
      );

      logger.debug('Payload encryption key initialized');
      return key;
    } catch (error) {
      logger.error('Failed to import encryption key', error);
      throw new Error('Failed to initialize encryption key');
    }
  }

  /**
   * Generate a cryptographically secure random IV
   */
  private generateIV(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));
  }

  /**
   * Encrypt a payload object
   *
   * @param payload - The object to encrypt
   * @returns Encrypted payload with IV
   * @throws Error if encryption fails or not enabled
   */
  public async encrypt(payload: DecryptedPayload): Promise<EncryptedPayload> {
    if (!this.enabled) {
      throw new Error('Payload encryption is not enabled');
    }

    try {
      const key = await this.initializeKey();
      const iv = this.generateIV();

      // Convert payload to JSON string, then to bytes
      const payloadString = JSON.stringify(payload);
      const payloadBytes = new TextEncoder().encode(payloadString);

      // Encrypt using AES-GCM
      const encryptedBuffer = await crypto.subtle.encrypt(
        { name: this.ALGORITHM, iv: iv as unknown as BufferSource },
        key,
        payloadBytes as unknown as BufferSource
      );

      // Convert to base64 for transport
      const encryptedArray = new Uint8Array(encryptedBuffer);
      const encryptedBase64 = btoa(String.fromCharCode(...encryptedArray));
      const ivBase64 = btoa(String.fromCharCode(...iv));

      return {
        data: encryptedBase64,
        iv: ivBase64,
        v: this.VERSION,
      };
    } catch (error) {
      logger.error('Payload encryption failed', error);
      throw new Error('Failed to encrypt payload');
    }
  }

  /**
   * Decrypt an encrypted payload
   *
   * @param encrypted - The encrypted payload to decrypt
   * @returns Decrypted payload object
   * @throws Error if decryption fails or not enabled
   */
  public async decrypt(encrypted: EncryptedPayload): Promise<DecryptedPayload> {
    if (!this.enabled) {
      throw new Error('Payload encryption is not enabled');
    }

    try {
      const key = await this.initializeKey();

      // Decode base64 to bytes
      const encryptedBytes = Uint8Array.from(atob(encrypted.data), (c) => c.charCodeAt(0));
      const iv = Uint8Array.from(atob(encrypted.iv), (c) => c.charCodeAt(0));

      // Decrypt using AES-GCM
      const decryptedBuffer = await crypto.subtle.decrypt(
        { name: this.ALGORITHM, iv },
        key,
        encryptedBytes
      );

      // Convert bytes back to JSON string, then parse
      const decryptedString = new TextDecoder().decode(decryptedBuffer);
      const payload = JSON.parse(decryptedString);

      return payload as DecryptedPayload;
    } catch (error) {
      logger.error('Payload decryption failed', error);
      throw new Error('Failed to decrypt payload');
    }
  }

  /**
   * Check if a response object is an encrypted payload
   */
  public isEncryptedPayload(data: unknown): data is EncryptedPayload {
    if (typeof data !== 'object' || data === null) {
      return false;
    }

    const payload = data as Record<string, unknown>;
    return (
      typeof payload.data === 'string' &&
      typeof payload.iv === 'string' &&
      typeof payload.v === 'number'
    );
  }
}

// Export singleton instance
export const payloadEncryption = new PayloadEncryption();

// Export class for testing
export { PayloadEncryption };
